import React, { useEffect, useState } from 'react';
import api from '../api/api';
import HostCleanerManager from '../components/cleaners/hostCleanerManager';
import { useUser } from '../context/UserContext';
import './cleaningMarketplace.css';

const AVAILABILITY_OPTIONS = ['Flexible', 'Weekdays', 'Weekends', 'Mon-Fri', 'Daily'];

const emptyForm = {
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    serviceRadius: 25,
    ratePerHour: '',
    flatRatePerClean: '',
    availability: 'Flexible',
    description: ''
};

const CleaningMarketplacePage = () => {
    const { user } = useUser();
    const [listings, setListings] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [filterCity, setFilterCity] = useState('');
    const [filterState, setFilterState] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const loadListings = async () => {
        try {
            const params = {};
            if (filterCity.trim()) params.city = filterCity.trim();
            if (filterState.trim()) params.state = filterState.trim();
            const res = await api.get('/cleaner', { params });
            setListings(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error('Failed to load listings', e);
            setListings([]);
        }
    };

    useEffect(() => {
        loadListings();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const openCreateModal = () => {
        setEditingId(null);
        setForm(emptyForm);
        setMessage({ type: '', text: '' });
        setShowModal(true);
    };

    const openEditModal = (listing) => {
        setEditingId(listing.id);
        setForm({
            companyName: listing.companyName || '',
            contactName: listing.contactName || '',
            email: listing.email || '',
            phone: listing.phone || '',
            city: listing.city || '',
            state: listing.state || '',
            serviceRadius: listing.serviceRadius ?? 25,
            ratePerHour: listing.ratePerHour ?? '',
            flatRatePerClean: listing.flatRatePerClean ?? '',
            availability: listing.availability || 'Flexible',
            description: listing.description || ''
        });
        setMessage({ type: '', text: '' });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingId(null);
        setForm(emptyForm);
        setMessage({ type: '', text: '' });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.companyName.trim() || !form.city.trim() || !form.state.trim()) {
            setMessage({ type: 'error', text: 'Company name, city, and state are required.' });
            return;
        }
        try {
            if (editingId) {
                await api.put(`/cleaner/${editingId}`, form);
                setMessage({ type: 'success', text: 'Listing updated.' });
            } else {
                await api.post('/cleaner', form);
                setMessage({ type: 'success', text: 'Listing posted!' });
            }
            await loadListings();
            setTimeout(closeModal, 1200);
        } catch (e) {
            console.error('Failed to save listing', e);
            setMessage({ type: 'error', text: 'Failed to save listing. Please try again.' });
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/cleaner/${id}`);
            setConfirmDeleteId(null);
            await loadListings();
        } catch (e) {
            console.error('Failed to delete listing', e);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        loadListings();
    };

    const formatRate = (listing) => {
        const parts = [];
        if (Number(listing.ratePerHour) > 0) parts.push(`$${Number(listing.ratePerHour).toFixed(2)}/hr`);
        if (Number(listing.flatRatePerClean) > 0) parts.push(`$${Number(listing.flatRatePerClean).toFixed(2)} flat`);
        return parts.length > 0 ? parts.join(' · ') : 'Contact for rates';
    };

    return (
        <div className="marketplace-container">
            {user?.role === 'host' && <HostCleanerManager />}
            <div className="marketplace-header">
                <div>
                    <h2>Cleaning Marketplace</h2>
                    <p className="marketplace-subtitle">Find professional cleaners for your short-term rental properties</p>
                </div>
                <button className="btn-primary" onClick={openCreateModal}>+ Post a Listing</button>
            </div>

            <form className="marketplace-filters" onSubmit={handleSearch}>
                <input
                    className="marketplace-filter-input"
                    type="text"
                    placeholder="Filter by city..."
                    value={filterCity}
                    onChange={(e) => setFilterCity(e.target.value)}
                />
                <input
                    className="marketplace-filter-input"
                    type="text"
                    placeholder="Filter by state..."
                    value={filterState}
                    onChange={(e) => setFilterState(e.target.value)}
                />
                <button type="submit" className="btn-secondary">Search</button>
                <button type="button" className="btn-ghost" onClick={() => { setFilterCity(''); setFilterState(''); setTimeout(loadListings, 0); }}>Clear</button>
            </form>

            {listings.length === 0 ? (
                <div className="marketplace-empty">
                    <p>No listings found. Be the first to post your cleaning service!</p>
                </div>
            ) : (
                <div className="marketplace-grid">
                    {listings.map((listing) => (
                        <div className="listing-card" key={listing.id}>
                            <div className="listing-card-header">
                                <h3 className="listing-company">{listing.companyName}</h3>
                                <span className="listing-availability-badge">{listing.availability}</span>
                            </div>
                            <div className="listing-location">
                                📍 {listing.city}, {listing.state} &nbsp;·&nbsp; {listing.serviceRadius} mi radius
                            </div>
                            <div className="listing-rate">{formatRate(listing)}</div>
                            {listing.description && (
                                <p className="listing-description">{listing.description}</p>
                            )}
                            <div className="listing-contact">
                                {listing.contactName && <span>👤 {listing.contactName}</span>}
                                {listing.phone && <span>📞 {listing.phone}</span>}
                                {listing.email && <span>✉️ {listing.email}</span>}
                            </div>
                            <div className="listing-actions">
                                <button className="btn-secondary btn-sm" onClick={() => openEditModal(listing)}>Edit</button>
                                <button className="btn-danger btn-sm" onClick={() => setConfirmDeleteId(listing.id)}>Delete</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create / Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-box marketplace-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingId ? 'Edit Listing' : 'Post a Cleaning Listing'}</h3>
                            <button className="modal-close" onClick={closeModal}>✕</button>
                        </div>

                        {message.text && (
                            <div className={`app-notice ${message.type === 'success' ? 'app-notice-success' : 'app-notice-error'}`}>
                                {message.text}
                            </div>
                        )}

                        <form className="marketplace-form" onSubmit={handleSubmit}>
                            <div className="form-row">
                                <label>Company Name *</label>
                                <input name="companyName" value={form.companyName} onChange={handleChange} placeholder="e.g. Sparkle Clean Co." />
                            </div>
                            <div className="form-row">
                                <label>Contact Name</label>
                                <input name="contactName" value={form.contactName} onChange={handleChange} placeholder="Full name" />
                            </div>
                            <div className="form-row-2col">
                                <div className="form-row">
                                    <label>Email</label>
                                    <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="contact@example.com" />
                                </div>
                                <div className="form-row">
                                    <label>Phone</label>
                                    <input name="phone" value={form.phone} onChange={handleChange} placeholder="(555) 000-0000" />
                                </div>
                            </div>
                            <div className="form-row-2col">
                                <div className="form-row">
                                    <label>City *</label>
                                    <input name="city" value={form.city} onChange={handleChange} placeholder="City" />
                                </div>
                                <div className="form-row">
                                    <label>State *</label>
                                    <input name="state" value={form.state} onChange={handleChange} placeholder="e.g. TX" maxLength={2} />
                                </div>
                            </div>
                            <div className="form-row">
                                <label>Service Radius (miles)</label>
                                <input name="serviceRadius" type="number" min="0" value={form.serviceRadius} onChange={handleChange} />
                            </div>
                            <div className="form-row-2col">
                                <div className="form-row">
                                    <label>Hourly Rate ($)</label>
                                    <input name="ratePerHour" type="number" min="0" step="0.01" value={form.ratePerHour} onChange={handleChange} placeholder="0.00" />
                                </div>
                                <div className="form-row">
                                    <label>Flat Rate Per Clean ($)</label>
                                    <input name="flatRatePerClean" type="number" min="0" step="0.01" value={form.flatRatePerClean} onChange={handleChange} placeholder="0.00" />
                                </div>
                            </div>
                            <div className="form-row">
                                <label>Availability</label>
                                <select name="availability" value={form.availability} onChange={handleChange}>
                                    {AVAILABILITY_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-row">
                                <label>Description</label>
                                <textarea name="description" value={form.description} onChange={handleChange} rows={4} placeholder="Tell hosts about your services, experience, specialties..." />
                            </div>
                            <div className="modal-footer-actions">
                                <button type="button" className="btn-ghost" onClick={closeModal}>Cancel</button>
                                <button type="submit" className="btn-primary">{editingId ? 'Save Changes' : 'Post Listing'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirm Delete */}
            {confirmDeleteId && (
                <div className="modal-overlay" onClick={() => setConfirmDeleteId(null)}>
                    <div className="modal-box confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Delete Listing?</h3>
                        <p>This action cannot be undone.</p>
                        <div className="modal-footer-actions">
                            <button className="btn-ghost" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                            <button className="btn-danger" onClick={() => handleDelete(confirmDeleteId)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CleaningMarketplacePage;
